import { OmitDeep } from 'type-fest';
import { z } from 'zod';

import { StylesConfig as PlayerStylesConfig } from '@player/content';

import { contentStylesDefaults } from './defaults';
import { colorValidator, logoFileValidator } from './validators';

enum TextPosition {
  Bottom = 'bottom',
  Center = 'center',
}

export const PlayerStylesConfigSchema = z.object({
  colors: z.object({
    primary: z
      .string()
      .refine(colorValidator)
      .default(contentStylesDefaults.colors.primary),
    secondary: z
      .string()
      .refine(colorValidator)
      .default(contentStylesDefaults.colors.secondary),
  }),
  controls: z.object({
    enabled: z.boolean().default(contentStylesDefaults.controls.enabled),
    progress_bar: z
      .boolean()
      .default(contentStylesDefaults.controls.progress_bar),
    play_button: z
      .boolean()
      .default(contentStylesDefaults.controls.play_button),
    fullscreen_button: z
      .boolean()
      .default(contentStylesDefaults.controls.fullscreen_button),
    mute_button: z
      .boolean()
      .default(contentStylesDefaults.controls.mute_button),
    navigation_buttons: z
      .boolean()
      .default(contentStylesDefaults.controls.navigation_buttons),
  }),
  link_button: z.object({
    enabled: z.boolean().default(contentStylesDefaults.link_button.enabled),
    color: z
      .string()
      .refine(colorValidator)
      .default(contentStylesDefaults.link_button.color),
    bg_color: z
      .string()
      .refine(colorValidator)
      .default(contentStylesDefaults.link_button.bg_color),
    text: z.string().default(contentStylesDefaults.link_button.text),
  }),
  text: z.object({
    font: z.string().trim().default(contentStylesDefaults.text.font),
    color: z
      .string()
      .refine(colorValidator)
      .default(contentStylesDefaults.text.color),
    font_size: z.coerce
      .number<number>()
      .positive()
      .default(contentStylesDefaults.text.font_size),
    bold: z.boolean().default(contentStylesDefaults.text.bold),
    italic: z.boolean().default(contentStylesDefaults.text.italic),
    position: z.enum(TextPosition).default(contentStylesDefaults.text.position),
  }),
  logo: z.object({
    enabled: z.boolean().default(contentStylesDefaults.logo.enabled),
    over_video: z.boolean().default(contentStylesDefaults.logo.over_video),
    size: z.number().positive().default(contentStylesDefaults.logo.size),
    opacity: z
      .number()
      .min(0)
      .max(100)
      .default(contentStylesDefaults.logo.opacity),
    position: z
      .templateLiteral([
        z.enum(['top', 'bottom']),
        '-',
        z.enum(['left', 'right']),
      ])
      .default(contentStylesDefaults.logo.position),
    file_path: z.string().default(contentStylesDefaults.logo.file_path),
  }),
  text_blocks_animation: z.object({
    style: z
      .enum(['horizontal', 'vertical', 'fade'])
      .default(contentStylesDefaults.text_blocks_animation.style),
  }),
  slides_animation: z.object({
    style: z
      .enum(['fade-in', 'zoom-in'])
      .default(contentStylesDefaults.slides_animation.style),
    duration: z.coerce
      .number<number>()
      .positive()
      .default(contentStylesDefaults.slides_animation.duration),
  }),
}) satisfies z.ZodType<
  OmitDeep<PlayerStylesConfig, 'logo.src'> & {
    // TODO: update player/loader to use mediaContentOrigin + path instead
    logo: { file_path: string };
  }
>;

export type StylesConfig = z.input<typeof PlayerStylesConfigSchema>;

export const uploadFilesSchema = z.object({
  logo_file: z.any().optional(),
  video_stub_file: z.any().optional(),
  thumbnail_stub_file: z.any().optional(),
});

const schema = z
  .object({
    name: z
      .string({
        error: (issue) =>
          issue.input === undefined ? 'Name is required' : undefined,
      })
      .trim()
      .min(5, {
        error: 'Name must be at least 5 characters',
      })
      .max(150, {
        error: 'Name must be at most 150 characters long',
      }),
    config: PlayerStylesConfigSchema,
    type: z.enum(['video', 'slider']),
  })
  .refine(logoFileValidator, {
    path: ['config.logo.enabled'],
    error: 'Logo file required if logo option is enabled.',
  });

export default schema;
